import { useState } from 'react';
import { X, Zap, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthModal = ({ isOpen, onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Logged in successfully!');
        onClose();
      } else if (mode === 'register') {
        await register(formData.email, formData.full_name, formData.password);
        toast.success('Account created successfully!');
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-health-ink/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
      data-testid="auth-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-[2rem] shadow-lift max-w-md w-full p-8 relative overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Accent Line */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-health-emerald via-health-amber to-health-coral"></div>
        
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:text-health-emerald hover:bg-health-mint transition-colors"
          data-testid="auth-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {mode === 'forgot' ? (
          <>
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-bold text-health-ink mb-2">Reset Password</h2>
              <p className="text-slate-500">Enter your email to receive a reset link.</p>
            </div>
            
            {forgotSent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-2xl bg-health-mint flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-health-emerald" />
                </div>
                <p className="text-health-ink font-medium mb-2">Email Sent!</p>
                <p className="text-slate-500 text-sm mb-6">Check your inbox for the reset link.</p>
                <Button
                  onClick={() => { setMode('login'); setForgotSent(false); }}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-health-mint"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <Label className="text-slate-600 text-sm">Email Address</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="pl-10 h-12 rounded-xl bg-health-mint/50 border-health-line text-health-ink placeholder:text-slate-500 focus:border-health-emerald"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-health-emerald hover:bg-health-emerald-dark text-white rounded-full font-bold py-6"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="w-full text-center text-sm text-slate-500 hover:text-health-emerald transition-colors"
                >
                  Back to login
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-bold text-health-ink mb-2">
                {mode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-slate-500">
                {mode === 'login' 
                  ? 'Sign in to access your account' 
                  : 'Join us and start shopping'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'register' && (
                <div>
                  <Label className="text-slate-600 text-sm">Full Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      placeholder="John Doe"
                      className="pl-10 h-12 rounded-xl bg-health-mint/50 border-health-line text-health-ink placeholder:text-slate-500 focus:border-health-emerald"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-slate-600 text-sm">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-xl bg-health-mint/50 border-health-line text-health-ink placeholder:text-slate-500 focus:border-health-emerald"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-600 text-sm">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="••••••••"
                    className="pl-10 h-12 rounded-xl bg-health-mint/50 border-health-line text-health-ink placeholder:text-slate-500 focus:border-health-emerald"
                    data-testid="password-input"
                  />
                </div>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => setMode('forgot')}
                    className="mt-2 text-sm text-slate-500 hover:text-health-emerald transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-health-emerald hover:bg-health-emerald-dark text-white rounded-full font-bold py-6 text-base"
                data-testid="auth-submit-btn"
              >
                {loading ? 'Processing...' : mode === 'login' ? 'Login' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <span className="text-slate-500 text-sm">
                {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
              </span>
              <button
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="text-health-emerald font-medium text-sm hover:underline"
              >
                {mode === 'login' ? 'Sign up' : 'Login'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
